import { type ProgressStep, ProgressStepState, ProgressStepperLayout } from '@lifebit-ai/lds'
import { filesize } from 'filesize'
import moment from 'moment'
import React from 'react'

import { getDestinationFolderLabel } from 'modules/requests/ImportRequestDetails/utils/getDestinationFolderLabel'
import { ImportActivityStage, ImportActivityStageState, ImportRequestStatus } from 'services/import-requests/enums'
import {
  getImportActivityStageTimestampIso,
  mapImportActivityTimelineStateToProgressStepState
} from 'services/import-requests/importActivityTimelineUtils'
import type { ExternalImportVerificationDetail, ImportActivityTimelineEntry } from 'services/import-requests/types'

import * as styles from './getExternalImportVerificationProgressStepperProps.styles'

export const EXTERNAL_IMPORT_PROGRESS_STEPPER_TEST_ID = 'external-import-timeline-test-id'
export const EXTERNAL_IMPORT_APPROVAL_REJECTION_MESSAGE_TEST_ID = 'external-import-approval-rejection-message'

export const EXTERNAL_IMPORT_PROGRESS_STEP_LABELS: Record<ImportActivityStage, string> = {
  [ImportActivityStage.Copy]: 'Copy to quarantine location',
  [ImportActivityStage.Scan]: 'Airlock security scan',
  [ImportActivityStage.Approval]: 'Airlock Manager approval',
  [ImportActivityStage.Available]: 'Available inside the workspace'
}

const VIRUS_AND_MALWARE_SCAN_DESCRIPTION = 'Copied objects are checked for threats before release to the workspace'

const AIRLOCK_MANAGER_APPROVAL_DESCRIPTION =
  'An Airlock Manager must approve the request before clean files are made available in this workspace. If the request is rejected, no files are shared.'

export interface ExternalImportVerificationProgressStep {
  stage: ImportActivityStage
  label: string
  state: ProgressStepState
  description: string
  subtitle?: string
}

export const getApprovalRejectionMessage = (rejectionReason?: string): string => {
  const trimmedReason = rejectionReason?.trim()

  if (trimmedReason) {
    return `Rejected by an Airlock Manager. Note: "${trimmedReason}"`
  }

  return 'A Data Custodian declined this import request.'
}

export const isRejectedApprovalStep = (
  step: ExternalImportVerificationProgressStep,
  request: ExternalImportVerificationDetail
): boolean =>
  step.stage === ImportActivityStage.Approval &&
  (request.status === ImportRequestStatus.Rejected || step.state === ProgressStepState.Failed)

const formatTotalSize = (totalSizeInBytes?: number): string =>
  totalSizeInBytes === undefined ? '0 B' : filesize(totalSizeInBytes, { round: 0, standard: 'jedec' })

const formatStepSubtitle = (timestamp: string, useTodayFormat = true): string => {
  const date = moment(timestamp)

  if (useTodayFormat && date.isSame(moment(), 'day')) {
    return `${date.format('HH:mm')} today`
  }

  return date.format('D MMM YYYY, HH:mm')
}

const getStepState = (
  entry: ImportActivityTimelineEntry,
  request: ExternalImportVerificationDetail
): ProgressStepState => {
  if (entry.stage === ImportActivityStage.Approval && request.status === ImportRequestStatus.Rejected) {
    return ProgressStepState.Failed
  }

  return mapImportActivityTimelineStateToProgressStepState(entry.state)
}

const getStageFailureReason = (
  request: ExternalImportVerificationDetail,
  stage: ImportActivityTimelineEntry
): string | undefined => {
  const isStageFailed = stage.state === ImportActivityStageState.Failed

  if (!isStageFailed) {
    return undefined
  }

  return request.failureReason !== undefined && request.failureReason !== ''
    ? request.failureReason
    : 'This step failed. Contact your administrator if the problem persists.'
}

const getCopyToQuarantineDescription = (
  request: ExternalImportVerificationDetail,
  copyStage: ImportActivityTimelineEntry
): string => {
  const copyFailureReason = getStageFailureReason(request, copyStage)

  if (copyFailureReason !== undefined) {
    return copyFailureReason
  }

  const sizeLabel = formatTotalSize(request.totalSizeInBytes)
  const totalObjects = request.counts?.total ?? 0
  const isCopyActive =
    copyStage.state === ImportActivityStageState.Active ||
    request.status === ImportRequestStatus.CopyingExternalCloudLocation

  if (isCopyActive) {
    return `Copying ${totalObjects.toLocaleString()} objects · ${sizeLabel} into the quarantine location.`
  }

  return `${totalObjects.toLocaleString()} objects · ${sizeLabel} copied to the quarantine location.`
}

const getVirusAndMalwareScanDescription = (
  request: ExternalImportVerificationDetail,
  scanStage: ImportActivityTimelineEntry
): string => {
  if (
    scanStage.state === ImportActivityStageState.Failed &&
    request.failureReason !== undefined &&
    request.failureReason !== ''
  ) {
    return request.failureReason
  }

  return VIRUS_AND_MALWARE_SCAN_DESCRIPTION
}

const getApprovalDescription = (
  request: ExternalImportVerificationDetail,
  approvalStage: ImportActivityTimelineEntry
): string => {
  if (request.status === ImportRequestStatus.Rejected || approvalStage.state === ImportActivityStageState.Failed) {
    return getApprovalRejectionMessage(request.rejectionReason)
  }

  if (approvalStage.state === ImportActivityStageState.Waiting) {
    return AIRLOCK_MANAGER_APPROVAL_DESCRIPTION
  }

  if (
    approvalStage.state === ImportActivityStageState.Complete ||
    approvalStage.state === ImportActivityStageState.Skipped
  ) {
    return approvalStage.state === ImportActivityStageState.Skipped
      ? 'Approval not required. The import proceeded automatically after scanning.'
      : AIRLOCK_MANAGER_APPROVAL_DESCRIPTION
  }

  return AIRLOCK_MANAGER_APPROVAL_DESCRIPTION
}

const getAvailableInWorkspaceDescription = (
  request: ExternalImportVerificationDetail,
  availableStage: ImportActivityTimelineEntry
): string => {
  const availableFailureReason = getStageFailureReason(request, availableStage)

  if (availableFailureReason !== undefined) {
    return availableFailureReason
  }

  const destinationFolder = getDestinationFolderLabel(request.destinationPrefix)

  if (availableStage.state === ImportActivityStageState.Complete || request.status === ImportRequestStatus.Available) {
    return 'Clean files are now available inside the Airlock In bucket.'
  }

  if (
    availableStage.state === ImportActivityStageState.Active ||
    request.status === ImportRequestStatus.CopyingToAirlockInBucket
  ) {
    return `Copying into ${destinationFolder}. The destination will be added to Whitelisted locations on completion.`
  }

  return 'Clean files are now available inside the Airlock In bucket.'
}

const getStepDescription = (entry: ImportActivityTimelineEntry, request: ExternalImportVerificationDetail): string => {
  switch (entry.stage) {
    case ImportActivityStage.Copy:
      return getCopyToQuarantineDescription(request, entry)
    case ImportActivityStage.Scan:
      return getVirusAndMalwareScanDescription(request, entry)
    case ImportActivityStage.Approval:
      return getApprovalDescription(request, entry)
    case ImportActivityStage.Available:
      return getAvailableInWorkspaceDescription(request, entry)
    default:
      return ''
  }
}

const getStepSubtitle = (
  entry: ImportActivityTimelineEntry,
  state: ProgressStepState,
  request: ExternalImportVerificationDetail
): string | undefined => {
  if (state === ProgressStepState.Pending) {
    return undefined
  }

  const legacyCompletedAt =
    entry.stage === ImportActivityStage.Copy
      ? request.copyCompletedAt
      : entry.stage === ImportActivityStage.Scan
      ? request.scanCompletedAt
      : undefined

  const timestamp = getImportActivityStageTimestampIso(entry, legacyCompletedAt)

  return timestamp !== undefined ? formatStepSubtitle(timestamp) : undefined
}

const mapTimelineEntryToProgressStep = (
  entry: ImportActivityTimelineEntry,
  request: ExternalImportVerificationDetail
): ExternalImportVerificationProgressStep => {
  const state = getStepState(entry, request)

  return {
    stage: entry.stage,
    label: EXTERNAL_IMPORT_PROGRESS_STEP_LABELS[entry.stage],
    state,
    description: getStepDescription(entry, request),
    subtitle: getStepSubtitle(entry, state, request)
  }
}

export const getExternalImportVerificationProgressSteps = (
  request: ExternalImportVerificationDetail
): ExternalImportVerificationProgressStep[] =>
  (request.timeline ?? []).map(entry => mapTimelineEntryToProgressStep(entry, request))

const mapProgressStep = (
  step: ExternalImportVerificationProgressStep,
  entryIndex: number,
  request: ExternalImportVerificationDetail
): ProgressStep => {
  const rejectedApproval = isRejectedApprovalStep(step, request)

  return {
    key: `${step.stage}-${entryIndex}`,
    state: step.state,
    label: step.label,
    subtitle: step.subtitle,
    description: rejectedApproval ? undefined : step.description,
    content: rejectedApproval ? (
      <styles.RejectionMessage data-testid={EXTERNAL_IMPORT_APPROVAL_REJECTION_MESSAGE_TEST_ID}>
        {getApprovalRejectionMessage(request.rejectionReason)}
      </styles.RejectionMessage>
    ) : undefined
  }
}

export const getExternalImportVerificationProgressStepperProps = (request: ExternalImportVerificationDetail) => ({
  layout: ProgressStepperLayout.Vertical,
  'data-testid': EXTERNAL_IMPORT_PROGRESS_STEPPER_TEST_ID,
  steps: getExternalImportVerificationProgressSteps(request).map((step, entryIndex) =>
    mapProgressStep(step, entryIndex, request)
  )
})
